import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const DashboardHeader = ({ user, stats }) => {
  const navigate = useNavigate();

  // Add safety check for user object
  if (!user || typeof user !== 'object') {
    return (
      <div className="bg-white rounded-2xl p-8 shadow-soft border border-accent-100 mb-8">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          <span className="ml-3 text-accent-600">Loading user information...</span>
        </div>
      </div>
    );
  }

  const handleProfileSettings = () => {
    navigate('/profile-settings');
  };

  // Helper function to get user's display name
  const getUserDisplayName = () => {
    if (user.first_name || user.last_name) {
      return `${user.first_name} ${user.last_name}`.trim();
    }
    return user.email?.split('@')[0] || 'User';
  };

  // Helper function to get user avatar
  const getUserAvatar = () => {
    if (user.profile_picture) {
      return user.profile_picture;
    }
    const name = getUserDisplayName();
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=f97316&color=ffffff&size=150`;
  };

  // Helper function to get member info
  const getMemberInfo = () => {
    const memberSince = user.date_joined ? new Date(user.date_joined).getFullYear() : new Date().getFullYear();
    const level = user.is_premium_member ? 'Premium Member' : 'Member';
    return `Member since ${memberSince} • ${level}`;
  };

  return (
    <div className="bg-gradient-to-br from-primary-50 to-secondary-50 rounded-2xl p-6 lg:p-8 mb-8">
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
        {/* User Profile Section */}
        <div className="flex items-center gap-4">
          <div className="relative">
            <Image
              src={getUserAvatar()}
              alt={getUserDisplayName()}
              className="w-16 h-16 lg:w-20 lg:h-20 rounded-full object-cover border-4 border-white shadow-warm"
            />
            {user.is_verified && (
              <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-success-400 rounded-full border-2 border-white flex items-center justify-center">
                <Icon name="Check" size={12} color="white" />
              </div>
            )}
          </div>
          <div>
            <h1 className="font-heading text-2xl lg:text-3xl font-bold text-accent-800 mb-1">
              Welcome back, {getUserDisplayName()}!
            </h1>
            <p className="text-accent-600 text-sm lg:text-base">
              {getMemberInfo()}
            </p>
            <div className="flex items-center gap-2 mt-2">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Icon
                    key={i}
                    name="Star"
                    size={14}
                    className={i < 4 ? "text-warning-400 fill-current" : "text-accent-300"}
                  />
                ))}
              </div>
              <span className="text-sm text-accent-600">(4.8/5)</span>
            </div>
            <div className="mt-3">
              <Button
                variant="outline"
                size="sm"
                iconName="Settings"
                iconPosition="left"
                onClick={handleProfileSettings}
                className="text-accent-600 hover:text-accent-800 border-accent-200 hover:border-accent-300"
              >
                Profile Settings
              </Button>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-4 lg:gap-6">
          {stats.map((stat, index) => (
            <div key={index} className="text-center">
              <div className="w-12 h-12 lg:w-14 lg:h-14 bg-white rounded-xl flex items-center justify-center shadow-soft mb-2 mx-auto">
                <Icon name={stat.icon} size={20} className="text-primary" />
              </div>
              <div className="text-xl lg:text-2xl font-bold text-accent-800">{stat.value}</div>
              <div className="text-xs lg:text-sm text-accent-600">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DashboardHeader;